// File: Appearance.js

import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useFormik } from "formik";
import { SketchPicker } from "react-color";

import {
  Box,
  Button,
  Divider as MuiDivider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
  Card as MuiCard,
  Checkbox,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { spacing } from "@mui/system";
import { CloudUpload as MuiCloudUpload } from "@mui/icons-material";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const CloudUpload = styled(MuiCloudUpload)(spacing);

const CenteredContent = styled.div`
  text-align: center;
`;

// Maximum file size - 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
// Optimal file size for compression - 1MB
const OPTIMAL_FILE_SIZE = 1 * 1024 * 1024;
// Allowed file types
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];

// Function to compress image
const compressImage = async (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        // For cover images, we want to maintain a wider aspect ratio
        if (width > 1200) {
          height = Math.round((height * 1200) / width);
          width = 1200;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with quality adjustment
        const quality = 0.8; // Slightly higher quality for cover images
        const base64String = canvas.toDataURL("image/jpeg", quality);
        resolve(base64String);
      };
    };
  });
};

// Default settings for appearance
const defaultSettings = {
  customBrandColors: false,
  lightThemeColor: "#3f51b5",
  darkThemeColor: "#7986cb",
  hideInstaconnectBranding: false,
  hideBookTeamMemberButton: false,
  theme: "system",
};

function Appearance({ team, onUpdate }) {
  const teamId = team?.id;

  // Initialize form data from team prop (backend data) or defaults
  const [formData, setFormData] = useState(() => {
    if (team) {
      return {
        customBrandColors:
          team.customBrandColors ?? defaultSettings.customBrandColors,
        lightThemeColor:
          team.lightThemeColor ?? defaultSettings.lightThemeColor,
        darkThemeColor: team.darkThemeColor ?? defaultSettings.darkThemeColor,
        hideInstaconnectBranding:
          team.hideInstaconnectBranding ??
          defaultSettings.hideInstaconnectBranding,
        hideBookTeamMemberButton:
          team.hideBookTeamMemberButton ??
          defaultSettings.hideBookTeamMemberButton,
        theme: team.theme ?? defaultSettings.theme,
      };
    }
    return defaultSettings;
  });

  const [colorPickerOpen, setColorPickerOpen] = useState({
    light: false,
    dark: false,
  });

  // Update form data when team prop changes (e.g., after data is fetched from backend)
  useEffect(() => {
    if (team) {
      console.log("Appearance: team prop changed, updating form data:", team);
      setFormData({
        customBrandColors:
          team.customBrandColors ?? defaultSettings.customBrandColors,
        lightThemeColor:
          team.lightThemeColor ?? defaultSettings.lightThemeColor,
        darkThemeColor: team.darkThemeColor ?? defaultSettings.darkThemeColor,
        hideInstaconnectBranding:
          team.hideInstaconnectBranding ??
          defaultSettings.hideInstaconnectBranding,
        hideBookTeamMemberButton:
          team.hideBookTeamMemberButton ??
          defaultSettings.hideBookTeamMemberButton,
        theme: team.theme ?? defaultSettings.theme,
      });
      setCoverImage(team.coverImage || null);
      setCoverImagePreview(team.coverImage || "");
    }
  }, [team]);

  const [coverImage, setCoverImage] = useState(team?.coverImage || null);
  const [coverImagePreview, setCoverImagePreview] = useState(
    team?.coverImage || ""
  );
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [imageError, setImageError] = useState("");
  const [uploading, setUploading] = useState(false);

  // Handle toggle changes
  const handleToggleChange = (event) => {
    const { name, checked } = event.target;
    setFormData({ ...formData, [name]: checked });
    setIsFormChanged(true);
  };

  // Handle theme selection
  const handleThemeChange = (event) => {
    setFormData({ ...formData, theme: event.target.value });
    setIsFormChanged(true);
  };

  // Handle color changes
  const handleColorChange = (colorType, color) => {
    setFormData({
      ...formData,
      [colorType === "light" ? "lightThemeColor" : "darkThemeColor"]: color.hex,
    });
    setIsFormChanged(true);
  };

  // Handle cover image upload using base64 conversion
  const handleCoverImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImageError("");

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setImageError("File size should not exceed 10MB");
      return;
    }

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setImageError("Please upload a valid image file (JPEG, PNG, or GIF)");
      return;
    }

    try {
      // Show loading state for large images
      if (file.size > OPTIMAL_FILE_SIZE) {
        setUploading(true);
      }

      // Compress image if it's larger than optimal size
      let imageData;
      if (file.size > OPTIMAL_FILE_SIZE) {
        imageData = await compressImage(file);
      } else {
        imageData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      }

      // Set both the preview and the actual image data
      setCoverImagePreview(imageData);
      setCoverImage(imageData);
      setIsFormChanged(true);
    } catch (error) {
      console.error("Image processing error:", error);
      setImageError("Failed to process image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Handle form submission - send all appearance settings to backend
  const handleSubmit = async () => {
    // Send all appearance settings to the backend
    const updateData = {
      coverImage: coverImage,
      customBrandColors: formData.customBrandColors,
      lightThemeColor: formData.lightThemeColor,
      darkThemeColor: formData.darkThemeColor,
      hideInstaconnectBranding: formData.hideInstaconnectBranding,
      hideBookTeamMemberButton: formData.hideBookTeamMemberButton,
      theme: formData.theme,
    };

    console.log("Saving appearance settings:", updateData);
    onUpdate(updateData);
    setIsFormChanged(false);
  };

  // Apply the appearance settings to the UI (this would be called from a central theme provider)
  useEffect(() => {
    // This is a placeholder for applying theme settings
    // In a real implementation, you'd update CSS variables or a theme context

    document.documentElement.dataset.theme = formData.theme;

    // Example: Set CSS variables for theme colors if customBrandColors is enabled
    if (formData.customBrandColors) {
      document.documentElement.style.setProperty(
        "--primary-light",
        formData.lightThemeColor
      );
      document.documentElement.style.setProperty(
        "--primary-dark",
        formData.darkThemeColor
      );
    }

    // Apply other settings as needed
  }, [formData]);

  return (
    <Box>
      <Typography variant="h3" gutterBottom>
        Booking Appearance
      </Typography>
      <Typography color="textSecondary" mb={4}>
        Configure the appearance of your team's booking page
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Booking Page Theme
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={3}>
          This only applies to your public booking pages
        </Typography>

        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: "center" }}>
              <Box
                component="img"
                src="/static/img/others/theme-system.svg"
                alt="System Theme"
                sx={{ mb: 1, maxWidth: "100%", height: 140 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.theme === "system"}
                    onChange={() => {
                      setFormData({ ...formData, theme: "system" });
                      setIsFormChanged(true);
                    }}
                  />
                }
                label="System default"
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: "center" }}>
              <Box
                component="img"
                src="/static/img/others/theme-light.svg"
                alt="Light Theme"
                sx={{ mb: 1, maxWidth: "100%", height: 140 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.theme === "light"}
                    onChange={() => {
                      setFormData({ ...formData, theme: "light" });
                      setIsFormChanged(true);
                    }}
                  />
                }
                label="Light"
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: "center" }}>
              <Box
                component="img"
                src="/static/img/others/theme-dark.svg"
                alt="Dark Theme"
                sx={{ mb: 1, maxWidth: "100%", height: 140 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.theme === "dark"}
                    onChange={() => {
                      setFormData({ ...formData, theme: "dark" });
                      setIsFormChanged(true);
                    }}
                  />
                }
                label="Dark"
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="subtitle1">Custom Brand Colors</Typography>
            <Typography variant="body2" color="textSecondary">
              Customize your own brand color into your booking page
            </Typography>
          </Box>
          <Switch
            name="customBrandColors"
            checked={formData.customBrandColors}
            onChange={handleToggleChange}
          />
        </Box>

        {formData.customBrandColors && (
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} sm={6}>
              <Box mb={1}>
                <Typography variant="subtitle2" gutterBottom>
                  Brand Color (Light Theme)
                </Typography>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      mr: 2,
                      bgcolor: formData.lightThemeColor,
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setColorPickerOpen({
                        ...colorPickerOpen,
                        light: !colorPickerOpen.light,
                      })
                    }
                  />
                  <TextField
                    value={formData.lightThemeColor}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        lightThemeColor: e.target.value,
                      });
                      setIsFormChanged(true);
                    }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                </Box>
                {colorPickerOpen.light && (
                  <Box sx={{ mt: 2, position: "relative", zIndex: 10 }}>
                    <Box
                      sx={{
                        position: "fixed",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                      }}
                      onClick={() =>
                        setColorPickerOpen({ ...colorPickerOpen, light: false })
                      }
                    />
                    <Box sx={{ position: "relative", zIndex: 11 }}>
                      <SketchPicker
                        color={formData.lightThemeColor}
                        onChangeComplete={(color) =>
                          handleColorChange("light", color)
                        }
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box mb={1}>
                <Typography variant="subtitle2" gutterBottom>
                  Brand Color (Dark Theme)
                </Typography>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      mr: 2,
                      bgcolor: formData.darkThemeColor,
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setColorPickerOpen({
                        ...colorPickerOpen,
                        dark: !colorPickerOpen.dark,
                      })
                    }
                  />
                  <TextField
                    value={formData.darkThemeColor}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        darkThemeColor: e.target.value,
                      });
                      setIsFormChanged(true);
                    }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                </Box>
                {colorPickerOpen.dark && (
                  <Box sx={{ mt: 2, position: "relative", zIndex: 10 }}>
                    <Box
                      sx={{
                        position: "fixed",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                      }}
                      onClick={() =>
                        setColorPickerOpen({ ...colorPickerOpen, dark: false })
                      }
                    />
                    <Box sx={{ position: "relative", zIndex: 11 }}>
                      <SketchPicker
                        color={formData.darkThemeColor}
                        onChangeComplete={(color) =>
                          handleColorChange("dark", color)
                        }
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="subtitle1">Team Cover Image</Typography>
            <Typography variant="body2" color="textSecondary">
              Add a cover image to your team page
            </Typography>
          </Box>
        </Box>

        <Box mb={3}>
          {coverImagePreview ? (
            <Box sx={{ position: "relative", mb: 2 }}>
              <Box
                component="img"
                src={coverImagePreview}
                alt="Cover Preview"
                sx={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                  borderRadius: 1,
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: 200,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No cover image set
              </Typography>
            </Box>
          )}

          <input
            accept="image/*"
            style={{ display: "none" }}
            id="cover-image-upload"
            type="file"
            onChange={handleCoverImageChange}
            disabled={uploading}
          />
          <label htmlFor="cover-image-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUpload />}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  Uploading...
                  <CircularProgress
                    size={20}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-10px",
                      marginLeft: "-10px",
                    }}
                  />
                </>
              ) : coverImagePreview ? (
                "Change Cover Image"
              ) : (
                "Upload Cover Image"
              )}
            </Button>
          </label>
          {imageError && (
            <Typography
              variant="caption"
              color="error"
              display="block"
              sx={{ mt: 1 }}
            >
              {imageError}
            </Typography>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Recommended size: 1200 x 300 pixels
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="subtitle1">
              Disable instaconnect.com branding
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Removes any instaconnect.com related brandings, i.e. 'Powered by
              Instaconnect.com.'
            </Typography>
          </Box>
          <Switch
            name="hideInstaconnectBranding"
            checked={formData.hideInstaconnectBranding}
            onChange={handleToggleChange}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1">
              Hide Book a Team Member Button
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Hide Book a Team Member Button from your public pages
            </Typography>
          </Box>
          <Switch
            name="hideBookTeamMemberButton"
            checked={formData.hideBookTeamMemberButton}
            onChange={handleToggleChange}
          />
        </Box>
      </Paper>

      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isFormChanged || uploading}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default Appearance;
